import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { entityScopeActionHintKey, type EntityScopeBlockReason } from "@/lib/entity-scope-state";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface EntityScopeBlockedTooltipProps {
  /** `useRollupWriteGuard().reason` - pass it straight through. */
  reason: EntityScopeBlockReason | null;
  children: ReactNode;
  className?: string;
}

/**
 * Says why a single disabled action is disabled.
 *
 * `EntityScopeRequired` is the whole-screen version of this. Not every
 * entity-scoped write owns a whole screen though - the Zoho organisation
 * picker, the Google Sheets settings save, the stuck-sync reset - and those
 * buttons were simply going grey with nothing on the page to say why. A
 * disabled control with no reason attached is indistinguishable from a broken
 * one, which is the same class of silent failure OCT-10873 set out to remove
 * from the read paths.
 *
 * Renders `children` untouched when `reason` is null, so a callsite can wrap
 * unconditionally rather than duplicating the button in two branches.
 *
 * The inner `span` is load-bearing. A disabled `button` emits no pointer
 * events, so Radix never sees the hover and the tooltip never opens; the span
 * is what the trigger actually attaches to.
 *
 * @spec ILLEGAL E89
 * @spec DOMAIN D23
 */
export function EntityScopeBlockedTooltip({
  reason,
  children,
  className,
}: EntityScopeBlockedTooltipProps) {
  const { t } = useTranslation("common");
  if (reason === null) return <>{children}</>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn("inline-flex", className)}>{children}</span>
      </TooltipTrigger>
      <TooltipContent data-testid="entity-scope-blocked-hint">
        {t(entityScopeActionHintKey(reason))}
      </TooltipContent>
    </Tooltip>
  );
}
